import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { toSnakeCase } from '@quickbit/backoffice-core-frontend/src/api/fetchAPI';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

const queryKeys = {
  all: () => ['algorand-processing'] as const,
  search: (searchBody: string) =>
    [...queryKeys.all(), 'search', searchBody] as const,
  allDetails: () => [...queryKeys.all(), 'allDetails'] as const,
  details: (referenceId: string) =>
    [...queryKeys.allDetails(), referenceId] as const,
};

export enum AlgorandProcessingRequestState {
  INITIATED = 'INITIATED',
  USER_ENSURED = 'USER_ENSURED',
  SENT_TO_USER = 'SENT_TO_USER',
  SENT_TO_POOL = 'SENT_TO_POOL',
  COMPLETED = 'COMPLETED',
}

export enum AlgorandTransactionStatus {
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  COMPLETED = 'COMPLETED',
}

export interface Filters {
  emailAddress?: string;
  referenceId?: string;
  state?: AlgorandProcessingRequestState;
  purchasedAtGt?: Date;
  purchasedAtLt?: Date;
}

export interface AlgorandProcessingRequest {
  referenceId: string;
  emailAddress: string;
  euroAmount: number;
  purchasedAt: Date | null;
  createdAt: Date;
  state: AlgorandProcessingRequestState;
  userId?: string;
}

export interface AlgorandProcessingRequestHistory {
  createdAt: Date;
  state: AlgorandProcessingRequestState;
}

export interface AlgorandProcessingTransaction {
  uuid: string;
  createdAt: Date;
  blockchainTransactionId: string;
  amount: number;
  networkFee: number;
  status: AlgorandTransactionStatus;
  senderAddress: string;
  receiverAddress: string;
}

export interface AlgorandProcessingRequestDetails
  extends AlgorandProcessingRequest {
  orderId: string;
  callbackUrl?: string;
  walletAddress?: string;
  history: AlgorandProcessingRequestHistory[];
  transactions: AlgorandProcessingTransaction[];
}

export interface SearchAlgorandProcessingRequestsResponse {
  aggregatedData: {
    count: number;
  };
  algorandProcessingRequests: AlgorandProcessingRequest[];
}

type Pagination = { size: number; page: number };

const getSearchQueryString = (filters: Filters, pagination: Pagination) => {
  const convertedFilters = {
    ...filters,
    purchasedAtGt: filters.purchasedAtGt
      ? filters.purchasedAtGt.toISOString()
      : undefined,
    purchasedAtLt: filters.purchasedAtLt
      ? filters.purchasedAtLt.toISOString()
      : undefined,
  };

  return new URLSearchParams(
    Object.fromEntries(
      Object.entries(toSnakeCase({ ...convertedFilters, ...pagination }))
        .filter(
          ([, value]) => value !== undefined && value !== null && value !== ''
        )
        .map(([key, value]) => [key, String(value)])
    )
  ).toString();
};

export const useSearch = (filters: Filters, pagination: Pagination) => {
  return useQuery({
    queryKey: queryKeys.search(JSON.stringify({ ...filters, ...pagination })),
    queryFn: async () => {
      const queryString = getSearchQueryString(filters, pagination);
      const data = await fetchAPI<SearchAlgorandProcessingRequestsResponse>(
        `v1/algorand-processing-requests?${queryString}`
      );
      return {
        ...data,
        algorandProcessingRequests: data.algorandProcessingRequests.map(
          (request) => ({
            ...request,
            purchasedAt: request.purchasedAt
              ? new Date(request.purchasedAt)
              : null,
            createdAt: new Date(request.createdAt),
          })
        ),
      };
    },
    placeholderData: keepPreviousData,
  });
};

export const retryAlgorandProcessingRequest = async (
  referenceId: string,
  useOldFlow: boolean,
  merchantId?: string
) => {
  const data = await fetchAPI<{ success: boolean }>(
    `v1/algorand-processing-requests/${referenceId}/retry`,
    {
      method: 'POST',
      body: { useOldFlow, ...(merchantId ? { merchantId } : {}) },
    }
  );
  return data.success;
};

export const useDetails = (referenceId: string) => {
  return useQuery({
    queryKey: queryKeys.details(referenceId),
    queryFn: async () => {
      const data = await fetchAPI<AlgorandProcessingRequestDetails>(
        `v1/algorand-processing-requests/${referenceId}`
      );

      return {
        ...data,
        purchasedAt: data.purchasedAt ? new Date(data.purchasedAt) : null,
        createdAt: new Date(data.createdAt),
        history: data.history.map((history) => ({
          ...history,
          createdAt: new Date(history.createdAt),
        })),
        transactions: data.transactions.map((transaction) => ({
          ...transaction,
          createdAt: new Date(transaction.createdAt),
        })),
      };
    },
  });
};
